import { useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import { api, type FpaRole, type FpaUserRow } from '../lib/api';
import {
  Card, CardHeader, Table, Th, Tr, Td, Badge, Select, Spinner, EmptyState, UserName,
} from '../components/ui';

const ROLE_LABEL: Record<string, string> = {
  '': 'No access',
  viewer: 'Viewer',
  analyst: 'Analyst',
  approver: 'Approver',
};

function RoleBadge({ role }: { role: FpaRole | null }) {
  if (!role) return <span style={{ color: 'var(--color-text-hint)', fontSize: 12 }}>—</span>;
  const variant = role === 'approver' ? 'blue' : role === 'analyst' ? 'green' : 'gray';
  return <Badge variant={variant}>{ROLE_LABEL[role]}</Badge>;
}

export default function AdminFpaAccessPage() {
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useQuery(() => api.fpa.users());
  const audit = useQuery(() => api.fpa.audit(50));
  const [savingId, setSavingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function changeRole(u: FpaUserRow, next: FpaRole | null) {
    setActionError(null);
    setSavingId(u.jira_user_id);
    try {
      await api.fpa.setRole(u.jira_user_id, next);
      refetch();
      audit.refetch();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setSavingId(null);
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div style={{ fontSize: 13, fontWeight: 500 }}>FP&amp;A access</div>
        </CardHeader>
        <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          FP&amp;A access is gated independently of admin rights — a full ORP admin has
          <strong> no </strong> FP&amp;A access unless granted a capability here. Granting any
          capability also grants company-wide data scope (the only scope enforced today; per-division
          and per-department scoping arrive in a later phase). Every change is recorded in the guarded
          FP&amp;A audit log below.
        </div>
        {actionError && (
          <div style={{
            margin: '0 16px 12px', padding: '8px 12px', fontSize: 12,
            background: 'var(--color-red-bg)', color: 'var(--color-red-text)',
            border: '0.5px solid var(--color-red-text)', borderRadius: 'var(--radius-sm)',
          }}>
            {actionError}
          </div>
        )}
        {isLoading ? <Spinner />
          : error ? <EmptyState message={error} />
          : !data || data.users.length === 0 ? <EmptyState message="No users." />
          : (
            <Table>
              <thead>
                <tr>
                  <Th>User</Th>
                  <Th>Org role</Th>
                  <Th>FP&amp;A capability</Th>
                  <Th>Scope</Th>
                  <Th>Change</Th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => {
                  const isSelf = u.jira_user_id === user?.jira_user_id;
                  const hasCompany = u.scopes.some((s) => s.scope_type === 'company');
                  return (
                    <Tr key={u.jira_user_id}>
                      <Td>
                        <UserName name={u.display_name} />
                        {isSelf && <span style={{ fontSize: 10, color: 'var(--color-text-hint)', marginLeft: 6 }}>(you)</span>}
                        <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{u.email}</div>
                      </Td>
                      <Td muted>{u.role}{u.is_admin ? ' · admin' : ''}</Td>
                      <Td><RoleBadge role={u.fpa_role} /></Td>
                      <Td>
                        {u.fpa_role
                          ? (hasCompany ? <Badge variant="gray">Company</Badge> : <span style={{ fontSize: 11, color: 'var(--color-amber-text)' }}>none</span>)
                          : <span style={{ color: 'var(--color-text-hint)', fontSize: 12 }}>—</span>}
                      </Td>
                      <Td>
                        <Select
                          value={u.fpa_role ?? ''}
                          onChange={(v) => changeRole(u, (v === '' ? null : v) as FpaRole | null)}
                        >
                          <option value="">No access</option>
                          <option value="viewer">Viewer</option>
                          <option value="analyst">Analyst</option>
                          <option value="approver">Approver</option>
                        </Select>
                        {savingId === u.jira_user_id && (
                          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 6 }}>saving…</span>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
      </Card>

      <Card>
        <CardHeader>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Recent FP&amp;A audit</div>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>access grants, scope changes, bootstrap</span>
        </CardHeader>
        {audit.isLoading ? <Spinner />
          : !audit.data || audit.data.entries.length === 0 ? <EmptyState message="No FP&A activity yet." />
          : (
            <Table>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Actor</Th>
                  <Th>Action</Th>
                  <Th>Target</Th>
                </tr>
              </thead>
              <tbody>
                {audit.data.entries.map((e) => (
                  <Tr key={e.id}>
                    <Td muted style={{ whiteSpace: 'nowrap' }}>{new Date(e.at).toLocaleString()}</Td>
                    <Td>
                      {e.actor_name ?? e.actor_id}
                      {e.real_actor_id && e.real_actor_id !== e.actor_id && (
                        <span style={{ fontSize: 10, color: 'var(--color-text-hint)', marginLeft: 4 }}>
                          (via {e.real_actor_name ?? e.real_actor_id})
                        </span>
                      )}
                    </Td>
                    <Td><Badge variant="gray">{e.action}</Badge></Td>
                    <Td muted>{e.entity_id ?? '—'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
      </Card>
    </>
  );
}
